import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Download, Loader2, ChevronDown } from "lucide-react";

export default function PresetGallery({ applyConfig, onImported }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || items.length) return;
    api.presetGallery().then((d) => setItems(d.gallery)).catch(() => {});
  }, [open, items.length]);

  const add = async (item) => {
    setBusy(item.slug);
    try {
      const p = await api.importGalleryPreset(item.slug);
      toast.success(`"${p.name}" saved to your presets`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add preset");
    } finally { setBusy(""); }
  };

  return (
    <div className="rounded-md border border-border bg-[#0E1420]/60 p-3.5 space-y-2.5" data-testid="preset-gallery">
      <button data-testid="toggle-preset-gallery" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-left">
        <Sparkles className="h-3.5 w-3.5 text-amber-400" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Starter library</span>
        <span className="ml-auto font-mono text-[10px] text-slate-600">ready-made setups</span>
        <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="space-y-1.5" data-testid="gallery-list">
          {items.length === 0 && <p className="font-mono text-[10px] text-slate-600">loading…</p>}
          {items.map((g) => (
            <div key={g.slug} data-testid={`gallery-item-${g.slug}`}
              className="rounded border border-border bg-[#0B0F18] px-2.5 py-2 space-y-1.5">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-xs text-slate-200">{g.title}</p>
                  <p className="text-[10px] text-slate-500 leading-snug">{g.blurb}</p>
                  <p className="font-mono text-[9px] text-slate-600 mt-0.5">
                    {(g.config.keywords || []).length ? `${g.config.keywords.length} kw · ` : ""}
                    {(g.config.seed_urls || []).length ? `${g.config.seed_urls.length} seed · ` : ""}
                    {(g.config.sitemap_urls || []).length ? "sitemap · " : ""}
                    {g.config.max_pages} pgs · {Math.round(g.config.time_limit_seconds / 60)}m · {g.config.extraction_mode}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="sm" data-testid={`add-gallery-${g.slug}`} onClick={() => add(g)} disabled={busy === g.slug}
                    className="h-6 px-2 bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold text-[10px]">
                    {busy === g.slug ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`load-gallery-${g.slug}`}
                    onClick={() => { applyConfig(g.config); toast.info(`Loaded "${g.title}" into the form`); }}
                    className="h-6 px-2 text-slate-400 hover:text-slate-200 text-[10px]">
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
